/**
 * Capture a deck slide DOM node (1920×1080 frame) to PNG using html2canvas.
 * Mirrors the photoslop studio export path: temporarily move off-screen at full
 * pixel size and clear CSS scale so the raster is sharp, not upscaled.
 */

import html2canvas from 'html2canvas';

const MAX_EXPORT_EDGE = 8192;

function prepareExportSize(
  element: HTMLElement,
  targetW: number,
  targetH: number,
): { effW: number; effH: number; restore: () => void } {
  let effW = Math.max(1, Math.round(targetW));
  let effH = Math.max(1, Math.round(targetH));
  const le = Math.max(effW, effH);
  if (le > MAX_EXPORT_EDGE) {
    const s = MAX_EXPORT_EDGE / le;
    effW = Math.max(1, Math.round(effW * s));
    effH = Math.max(1, Math.round(effH * s));
  }

  const prev = {
    width: element.style.width,
    height: element.style.height,
    maxWidth: element.style.maxWidth,
    maxHeight: element.style.maxHeight,
    minWidth: element.style.minWidth,
    minHeight: element.style.minHeight,
    flex: element.style.flex,
    flexShrink: element.style.flexShrink,
    position: element.style.position,
    left: element.style.left,
    top: element.style.top,
    right: element.style.right,
    zIndex: element.style.zIndex,
    boxShadow: element.style.boxShadow,
    margin: element.style.margin,
    alignSelf: element.style.alignSelf,
  };

  element.style.width = `${effW}px`;
  element.style.height = `${effH}px`;
  element.style.maxWidth = 'none';
  element.style.maxHeight = 'none';
  element.style.minWidth = '0';
  element.style.minHeight = '0';
  element.style.flex = 'none';
  element.style.flexShrink = '0';
  element.style.position = 'fixed';
  element.style.left = '-99999px';
  element.style.top = '0';
  element.style.right = 'auto';
  element.style.zIndex = '2147483646';
  element.style.boxShadow = 'none';
  element.style.margin = '0';
  element.style.alignSelf = 'auto';

  const restore = () => {
    element.style.width = prev.width;
    element.style.height = prev.height;
    element.style.maxWidth = prev.maxWidth;
    element.style.maxHeight = prev.maxHeight;
    element.style.minWidth = prev.minWidth;
    element.style.minHeight = prev.minHeight;
    element.style.flex = prev.flex;
    element.style.flexShrink = prev.flexShrink;
    element.style.position = prev.position;
    element.style.left = prev.left;
    element.style.top = prev.top;
    element.style.right = prev.right;
    element.style.zIndex = prev.zIndex;
    element.style.boxShadow = prev.boxShadow;
    element.style.margin = prev.margin;
    element.style.alignSelf = prev.alignSelf;
  };

  return { effW, effH, restore };
}

function drawSnapshotCover(
  ctx: CanvasRenderingContext2D,
  snapshot: HTMLCanvasElement,
  targetW: number,
  targetH: number,
) {
  const iw = snapshot.width;
  const ih = snapshot.height;
  if (iw < 1 || ih < 1) return;
  const tr = targetW / targetH;
  const ir = iw / ih;
  let sx: number;
  let sy: number;
  let sw: number;
  let sh: number;
  if (ir > tr) {
    sh = ih;
    sw = Math.round(ih * tr);
    sx = (iw - sw) / 2;
    sy = 0;
  } else {
    sw = iw;
    sh = Math.round(iw / tr);
    sx = 0;
    sy = (ih - sh) / 2;
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(snapshot, sx, sy, sw, sh, 0, 0, targetW, targetH);
}

export async function captureDeckSlideFrameToPngBlob(
  element: HTMLElement,
  targetW = 1920,
  targetH = 1080,
): Promise<Blob> {
  if (typeof document === 'undefined') {
    throw new Error('Export is only available in the browser.');
  }
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

  const prevTransform = element.style.transform;
  const prevTransformOrigin = element.style.transformOrigin;
  element.style.transform = 'none';
  element.style.transformOrigin = 'top left';

  const prep = prepareExportSize(element, targetW, targetH);
  try {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    const rect = element.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) {
      throw new Error('Slide frame is too small to export.');
    }

    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const supersample = Math.min(4, Math.max(2, dpr));
    const scale = Math.min(8, Math.max(1, supersample * (prep.effW / Math.max(rect.width, 1))));

    const snapshot = await html2canvas(element, {
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      foreignObjectRendering: false,
      imageTimeout: 20000,
    });

    const out = document.createElement('canvas');
    out.width = prep.effW;
    out.height = prep.effH;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Could not create export canvas');
    drawSnapshotCover(ctx, snapshot, prep.effW, prep.effH);

    const blob = await new Promise<Blob | null>((resolve) => {
      out.toBlob(resolve, 'image/png', 1.0);
    });
    if (!blob) throw new Error('PNG encoding failed');
    return blob;
  } finally {
    prep.restore();
    element.style.transform = prevTransform;
    element.style.transformOrigin = prevTransformOrigin;
  }
}
